import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { TdgDataTableComponent } from '../../../components/tdg/tdg-data-table/tdg-data-table.component';
import { TdgVersionControlComponent } from '../../../components/tdg/tdg-version-control/tdg-version-control.component';
import {
  GenerateTestDataModalComponent,
  GenerateTestDataModalResult,
} from '../../../components/modal-templates/generate-test-data-modal/generate-test-data-modal.component';
import {
  ImportTestDataModalComponent,
  ImportTestDataModalResult,
} from '../../../components/modal-templates/import-test-data-modal/import-test-data-modal.component';
import { BaseLayoutComponent } from '../../base-layout.component';

type TdgActiveView = 'versionControl' | 'dataTable';

@Component({
  selector: 'app-tdg',
  standalone: true,
  imports: [CommonModule, TdgVersionControlComponent, TdgDataTableComponent],
  templateUrl: './tdg.component.html',
  styleUrl: './tdg.component.scss'
})
export class TdgComponent extends BaseLayoutComponent implements OnInit, OnDestroy {
  private static readonly GENERATE_ACTION_KEY = 'generateTestData';
  private static readonly IMPORT_ACTION_KEY = 'importTestData';

  @ViewChild(TdgVersionControlComponent)
  private versionControlRef?: TdgVersionControlComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly modalService = inject(NgbModal);
  private selectedSchemas: any = [];
  private projectId = '';

  activeView: TdgActiveView = 'versionControl';
  private isTestCasePreview = false;

  ngOnInit() {
    this.projectService.getProjectId().subscribe((projId: any) => {
      this.projectId = projId;
      this.tdgService.setProjectId(projId);
    });

    this.syncHeaderActions();

    this.breadcrumbService
      .onPageAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe((key: string) => {
        if (key === TdgComponent.GENERATE_ACTION_KEY) {
          this.onGenerateTestData();
        } else if (key === TdgComponent.IMPORT_ACTION_KEY) {
          this.onImportTestData();
        }
      });
  }

  ngOnDestroy(): void {
    this.breadcrumbService.clearPageActions();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setSelectedSchema(schemas: any[]) {
    this.selectedSchemas = schemas;
  }

  getSelectedSchemas() {
    return this.selectedSchemas;
  }

  onGenerateTestData(): void {
    const modalRef = this.modalService.open(GenerateTestDataModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'lg',
      keyboard: false,
      windowClass: 'generate-test-data-modal-window',
    });
    modalRef.componentInstance.projectId =
      this.projectId || this.tdgService.getProjectId();

    modalRef.result
      .then((result: GenerateTestDataModalResult) => {
        if (!result?.testCases?.length) {
          return;
        }

        if (result.source === 'liveDb') {
          if (!result.dbConnection) {
            return;
          }
          const connectionId =
            result.dbConnection?.id ||
            '';
          this.tdgService.setSelectedFile({
            file: result.dbConnection,
            type: 'DATABASE',
          } as any);
          this.tdgService.setSelectedFileName(connectionId);
        } else {
          if (!result.file) {
            return;
          }
          this.tdgService.setSelectedFile({
            file: result.file,
            type: result.fileType,
          } as any);
          this.tdgService.setSelectedFileName(
            result.file?.id || result.file?._id || ''
          );
        }

        this.tdgService.setRetrievedSchemas([]);
        this.tdgService.setSelectedTestSuite({
          dataSource: result.dataSource,
          testSuite: result.testSuite,
          testCases: result.testCases,
        });

        this.activeView = 'dataTable';
        this.syncHeaderActions();
      })
      .catch(() => {
        // dismissed — no-op
      });
  }

  onImportTestData(): void {
    const modalRef = this.modalService.open(ImportTestDataModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'lg',
      keyboard: false,
      windowClass: 'import-test-data-modal-window',
    });
    modalRef.componentInstance.projectId =
      this.projectId || this.tdgService.getProjectId();

    modalRef.result
      .then((result: ImportTestDataModalResult) => {
        if (!result?.file || !result?.testSuiteId) {
          return;
        }
        if (result.message) {
          this.toaster.pop({ type: "success", text: result.message });
        }
        this.versionControlRef?.refresh();
      })
      .catch(() => {
        // dismissed — no-op
      });
  }

  onBackToVersionControl(): void {
    this.activeView = 'versionControl';
    this.isTestCasePreview = false;
    this.syncHeaderActions();
  }

  onTestCasePreview(inPreview: boolean): void {
    this.isTestCasePreview = !!inPreview;
    this.syncHeaderActions();
  }

  private syncHeaderActions(): void {
    if (this.activeView !== 'versionControl' || this.isTestCasePreview) {
      this.breadcrumbService.clearPageActions();
      return;
    }

    this.breadcrumbService.setPageActions([
      {
        key: 'addTestData',
        label: 'Generate Test Data',
        variant: 'primary',
        isDropdown: true,
        menuItems: [
          {
            key: TdgComponent.GENERATE_ACTION_KEY,
            label: 'Generate Test Data',
          },
          {
            key: TdgComponent.IMPORT_ACTION_KEY,
            label: 'Import Test Data',
          },
        ],
      },
    ]);
  }
}
